import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SupplierSite } from './supplierSite.model';
import { Page } from '../../../models/page-wrapper.model';
import { environment } from '../../../../environments/environment';
import { PickerRecentCacheService } from '../../../shared/services/picker-recent-cache.service';
import { EntityDropdownPickerAdapter } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { PickerRecentRecord } from '../../../shared/components/entity-dropdown-picker/picker-recent.model';

@Injectable({
  providedIn: 'root'
})
export class SupplierSiteService {
  private apiUrl = `${environment.restApiUrl}masterdata/supplier-sites`;
  private readonly pickerEntityKey = 'supplier-site';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) { }

  private handleError(error: HttpErrorResponse) {
    // A client-side or network error occurred.
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${JSON.stringify(error.error)}`
      );
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something went wrong; please try again later.'));
  }

  getAllSupplierSites(params?: any): Observable<Page<SupplierSite>> {
    return this.http.get<Page<SupplierSite>>(this.apiUrl + '/search', { params }).pipe(catchError(this.handleError));
  }

  getSupplierSiteById(id: number): Observable<SupplierSite> {
    return this.http.get<SupplierSite>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  searchSupplierSites(query: string, page = 0, size = 25): Observable<Page<SupplierSite>> {
    return forkJoin([
      this.searchSupplierSitesByField('supplierSiteName', query, page, size),
      this.searchSupplierSitesByField('supplierSiteNumber', query, page, size),
    ]).pipe(
      map(([namePage, numberPage]) => this.mergeSupplierSitePages(namePage, numberPage, page, size)),
      catchError(this.handleError)
    );
  }

  loadRecentSupplierSites(limit = 10): Observable<SupplierSite[]> {
    return from(this.recentPickerCache.loadRecent<SupplierSite>(this.pickerEntityKey, limit));
  }

  getFreshSupplierSiteById(id: number | string): Observable<SupplierSite> {
    return this.getSupplierSiteById(Number(id));
  }

  rememberSupplierSite(supplierSite: SupplierSite): Observable<void> {
    const record: PickerRecentRecord<SupplierSite> = {
      key: `${this.pickerEntityKey}::${supplierSite.supplierSiteId}`,
      entityKey: this.pickerEntityKey,
      userKey: this.getCurrentUserKey(),
      entityId: supplierSite.supplierSiteId,
      label: `${supplierSite.supplierSiteName ?? ''}${supplierSite.supplierSiteNumber ? ` - ${supplierSite.supplierSiteNumber}` : ''}`.trim(),
      payload: supplierSite,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createSupplierSitePickerAdapter(): EntityDropdownPickerAdapter<SupplierSite> {
    return {
      entityKey: this.pickerEntityKey,
      loadRecent: (limit?: number) => this.loadRecentSupplierSites(limit),
      search: (query: string, page: number, size: number) => this.searchSupplierSites(query, page, size),
      getFreshById: (id: number | string) => this.getFreshSupplierSiteById(id),
      rememberSelection: (supplierSite: SupplierSite) => this.rememberSupplierSite(supplierSite),
      getId: (supplierSite: SupplierSite) => supplierSite.supplierSiteId,
      getLabel: (supplierSite: SupplierSite) => `${supplierSite.supplierSiteName ?? ''}${supplierSite.supplierSiteNumber ? ` - ${supplierSite.supplierSiteNumber}` : ''}`.trim(),
      getSecondaryLabel: (supplierSite: SupplierSite) => supplierSite.supplier?.supplierName ? `Supplier: ${supplierSite.supplier.supplierName}` : '',
      filterBy: 'supplierSiteName,supplierSiteNumber',
    };
  }

  createSupplierSite(supplierSite: Partial<SupplierSite>): Observable<SupplierSite> {
    return this.http.post<SupplierSite>(this.apiUrl, supplierSite).pipe(catchError(this.handleError));
  }

  updateSupplierSite(supplierSite: Partial<SupplierSite>): Observable<SupplierSite> {
    return this.http
      .put<SupplierSite>(`${this.apiUrl}/${supplierSite.supplierSiteId}`, supplierSite)
      .pipe(catchError(this.handleError));
  }

  deleteSupplierSite(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }

  private searchSupplierSitesByField(
    field: 'supplierSiteName' | 'supplierSiteNumber',
    query: string,
    page: number,
    size: number
  ): Observable<Page<SupplierSite>> {
    const params = {
      [field]: query,
      page,
      size,
    };

    return this.http.get<Page<SupplierSite>>(`${this.apiUrl}/search`, { params }).pipe(
      catchError(() => of(this.emptySupplierSitePage(page, size)))
    );
  }

  private mergeSupplierSitePages(
    firstPage: Page<SupplierSite>,
    secondPage: Page<SupplierSite>,
    page: number,
    size: number
  ): Page<SupplierSite> {
    const merged = new Map<number, SupplierSite>();
    [...(firstPage.content || []), ...(secondPage.content || [])].forEach((supplierSite) => {
      if (supplierSite?.supplierSiteId !== undefined && supplierSite?.supplierSiteId !== null) {
        merged.set(supplierSite.supplierSiteId, supplierSite);
      }
    });

    const content = Array.from(merged.values()).slice(0, size);

    return {
      ...firstPage,
      content,
      number: page,
      size,
      totalElements: merged.size,
      numberOfElements: content.length,
    };
  }

  private emptySupplierSitePage(page: number, size: number): Page<SupplierSite> {
    return {
      content: [],
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: true,
      totalPages: 0,
      totalElements: 0,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: 0,
      empty: true,
    };
  }
}
